import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useGlobal } from '../contexts/GlobalContext';
import { useBuild } from '../contexts/BuildContext';

const exampleQuery = `
{
  "query": {
    "bool": {
      "must": [
        {
          "terms": {
            "publication_type": ["zora_video", "video"]
          }
        }
      ]
    }
  },
  "sort": [
    {
      "score_trending": "desc"
    }
  ],
  "size": 10
}
`


const ESQueryQuery = () => {
  const { mergeCurrentContext } = useGlobal();
  const { searchApi } = useBuild();
  
  // Form state
  const [queryJson, setQueryJson] = useState(exampleQuery);
  
  // Query execution state
  const [isExecuting, setIsExecuting] = useState(false);
  const [jsonError, setJsonError] = useState('');

  useEffect(() => {
    mergeCurrentContext({
      queryBeingBuilt: { queryJson },
    });
    return () => mergeCurrentContext({ queryBeingBuilt: undefined });
  }, [queryJson, mergeCurrentContext]);

  const validateJson = (jsonString) => {
    try {
      JSON.parse(jsonString);
      setJsonError('');
      return true;
    } catch (error) {
      setJsonError(`Invalid JSON: ${error.message}`);
      return false;
    }
  };

  const handleQueryChange = (event) => {
    const newQuery = event.target.value;
    setQueryJson(newQuery);
    validateJson(newQuery);
  };

  const handleExecuteQuery = async () => {
    if (!queryJson.trim()) {
      alert('Please enter an Elasticsearch query');
      return;
    }

    if (!validateJson(queryJson)) {
      alert('Please fix the JSON syntax errors before executing');
      return;
    }

    const payload = {
      query: JSON.parse(queryJson),
    };
    
    console.log('ES Query Payload:', payload);
    
    try {
      setIsExecuting(true);
      const response = await searchApi.generateEsQueryCandidates(payload);
      console.log('API Response:', response);
      // Results are automatically stored in context by the API method
    } catch (error) {
      console.error('Error executing Elasticsearch query:', error);
      // Error is automatically handled by the API method in context
    } finally {
      setIsExecuting(false);
    }
  };

  const handleFormatJson = () => {
    try {
      const parsed = JSON.parse(queryJson);
      setQueryJson(JSON.stringify(parsed, null, 2));
      setJsonError('');
    } catch (error) {
      setJsonError(`Cannot format: ${error.message}`);
    }
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Elasticsearch Query
      </Typography>
      
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Compose your Elasticsearch query in JSON format:
        </Typography>
        
        {jsonError && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {jsonError}
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={handleFormatJson}
            disabled={!!jsonError}
          >
            Format JSON
          </Button>
        </Box>
        
        <TextField
          label="Elasticsearch Query (JSON)"
          multiline
          rows={16}
          value={queryJson}
          onChange={handleQueryChange}
          variant="outlined"
          sx={{ flex: 1 }}
          error={!!jsonError}
          helperText={jsonError ? jsonError : "Enter a valid Elasticsearch query in JSON format"}
        />
      </Box>

      {/* Execute Button */}
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={handleExecuteQuery}
          disabled={!queryJson.trim() || isExecuting || !!jsonError}
          sx={{ minWidth: 200 }}
        >
          {isExecuting ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              Executing...
            </>
          ) : (
            'Execute Query'
          )}
        </Button>
      </Box>
    </Box>
  );
};

export default ESQueryQuery;
